/* eslint-disable react/jsx-no-useless-fragment */
import React, { FC, useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import i18n, { setLang } from 'i18n';
import { useLocation, useNavigate } from 'react-router-dom';
import { RenderIf } from 'components';
import PreloaderComponent from 'components/Preloader/Preloader.component';
import useUserValidation from 'modules/Chat/hooks/useUserValidation';
import { CHAT_LOCAL_STORAGE_CHAT_USER } from 'modules/Chat/Chat.values';
import { LOCAL_STORAGE_AUTH_TOKEN, LOCAL_STORAGE_USER_ID } from 'common/localStorageKeys';
import ROUTE_PATHS from 'routes/values';
import moment from 'moment';

enum IEQueryParams {
  TOKEN = 'tokenAccess',
  LANGUAGE_VALUE = 'LanguageValue',
}

const TokenAccessMiddleware: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get(IEQueryParams.TOKEN);
  const [finish, setFinish] = useState(false);

  const { validate, isSuccess, data, isFinished } = useUserValidation();
  const languageValue = queryParams.get(IEQueryParams.LANGUAGE_VALUE);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentLang, setCurrentLang] = useState<string>(i18n.language || 'en');
  const setLanguage = (lang: string): Promise<TFunction<'translation', undefined>> => setLang(lang);
  const [languageReady, setLanguageReady] = useState(false);

  useEffect(() => {

    const applyLanguage = async (): Promise<void> => {
      if (languageValue) {
        await setLanguage(languageValue);
        setCurrentLang(languageValue);
      }
      setLanguageReady(true);
    };

    applyLanguage();

    if (!token) {
      setFinish(true);
    } else {
      validate({
        email: '',
        accessCode: '',
        name: '',
        lastname: '',
        token,
      });
    }
  }, [token, languageValue]);

  useEffect(() => {
    if (isFinished) {
      const newQueryParams = queryParams;
      newQueryParams.delete(IEQueryParams.TOKEN);
      newQueryParams.delete(IEQueryParams.LANGUAGE_VALUE);

      if (isSuccess && data?.token === token) {
        window.localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, data?.token as string);
        window.localStorage.setItem(LOCAL_STORAGE_USER_ID, 'anonymousUser');
        if (languageValue) {
          window.localStorage.setItem(IEQueryParams.LANGUAGE_VALUE, languageValue);
        }
        const newData = {
          id: data?.external_id || "anonymousUser",
          created: moment().toString(),
          token: data?.token,
          email: "anonymousUser@anonymousUser.com",
          name: "anonymousUser",
          lastname: "anonymousUser",
          kineduUser: data?.kinedu_user || false,
          paymentToken: data?.token as string,
        };

        window.localStorage.setItem(CHAT_LOCAL_STORAGE_CHAT_USER, JSON.stringify(newData));

        navigate({
          search: newQueryParams.toString(),
        });
      } else {
        navigate({
          pathname: ROUTE_PATHS.SIGN_UP,
          search: newQueryParams.toString(),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinished, isSuccess]);

  return (
    <RenderIf condition={finish && languageReady} alternative={<PreloaderComponent />}>
      <>{children}</>
    </RenderIf>
  );
};

export default TokenAccessMiddleware;
